import React from 'react'
import styled from 'styled-components'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { TransactionalDialog } from '@jarvis.oddle/core/components/Dialog'
import { P, Text } from '@jarvis.oddle/core/components/Typo'

import { makeCombineString } from 'src/utils/string'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'

const ListItem = styled.li``

const UnorderedList = styled.ul`
  text-align: left;
`

const createPromotionName = (promotionName, isMultipleFailedPromotions) =>
  makeCombineString('')(
    isMultipleFailedPromotions ? null : '(',
    promotionName,
    isMultipleFailedPromotions ? null : ')'
  )

const CheckoutFailedPromotionModal = ({
  redirectToCheckoutPage,
  failedPromotions = [],
  ...props
}: {
  redirectToCheckoutPage: () => void
  failedPromotions: {
    id: string
    error: string
  }[]
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const selectedPromotions = useAppSelector(selectOrder).selectedPromotions

  const isMultipleFailedPromotions = failedPromotions.length > 1
  const Item = isMultipleFailedPromotions ? ListItem : Text
  const ItemWrapper = isMultipleFailedPromotions
    ? UnorderedList
    : React.Fragment
  const preposition = isMultipleFailedPromotions
    ? t('You are currently not eligible for the following promotions:')
    : t('You are currently not eligible for this promotion ')

  return (
    <TransactionalDialog
      {...props}
      onConfirm={redirectToCheckoutPage}
      onCancel={() => modalController.showModal('CheckoutPromotionModal')}
      cancelText={t('No')}
      confirmText={t('Yes')}
      header={t('Oops')}
      body={
        <React.Fragment>
          <Text>{preposition}</Text>
          <ItemWrapper>
            {failedPromotions.map((failedPromo) => {
              const promotion = (selectedPromotions.data || []).find(
                (selectedPromo) => selectedPromo.id === failedPromo.id
              )

              if (!promotion) return null

              const promotionName = (
                <b>
                  {createPromotionName(
                    promotion.name,
                    isMultipleFailedPromotions
                  )}
                </b>
              )
              let errorText
              switch (failedPromo.error) {
                case 'promotion_promo_first_time':
                  errorText = isMultipleFailedPromotions
                    ? t(
                        '_promotionName_ - only applicable for first-time customers.',
                        { promotionName }
                      )
                    : t(
                        '_promotionName_ as the promotion is only applicable for first-time customers.',
                        { promotionName }
                      )
                  break
                case 'promotion_promo_repeated':
                  errorText = isMultipleFailedPromotions
                    ? t(
                        '_promotionName_ - only applicable for repeated customers.',
                        { promotionName }
                      )
                    : t(
                        '_promotionName_ as the promotion is only applicable for repeated customers.',
                        { promotionName }
                      )
                  break
                case 'promotion_promo_use_once':
                  errorText = isMultipleFailedPromotions
                    ? t(
                        '_promotionName_ - only entitled to use the promotion once.',
                        { promotionName }
                      )
                    : t(
                        '_promotionName_ as each customer is only entitled to use the promotion once.',
                        { promotionName }
                      )
                  break
                default:
                  errorText = isMultipleFailedPromotions
                    ? t('_promotionName_ - no longer eligible.', {
                        promotionName
                      })
                    : t('_promotionName_ as it is no longer eligible.', {
                        promotionName
                      })
              }
              return <Item>{errorText}</Item>
            })}
          </ItemWrapper>
        </React.Fragment>
      }
      subText={
        <P normalised textAlign='center'>
          {isMultipleFailedPromotions
            ? t(
                'Would you like to remove the above promotions and continue to checkout?'
              )
            : t(
                'Would you like to remove this promotion and continue to checkout?'
              )}
        </P>
      }
    />
  )
}

export default makeControlledModal(
  CheckoutFailedPromotionModal,
  'CheckoutFailedPromotionModal'
)
